import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components/macro';
import colors from 'Common/constants/colors';
import {
    ActionBar,
    ActionBarButtonsWrapper,
    ActionBarTitle,
} from 'Common/components/actionBar';
import Button from 'Common/components/Button';
import goToNextExamQuestion from 'Education/api/instruction-assignment/exam/goToNextExamQuestion';
import completeSingleInstructionAssignmentExam from 'Education/api/instruction-assignment/exam/completeSingleInstructionAssignmentExam';
import QuestionTitle from 'Education/components/instruction-assignment/exam/QuestionTitle';
import AnswerChoices from 'Education/components/instruction-assignment/exam/AnswerChoices';
import goToPreviousExamQuestion from 'Education/api/instruction-assignment/exam/goToPreviousExamQuestion';
import Loader from 'Common/components/Loader';
import updateInstructionAssignmentExamGivenAnswers from 'Education/api/instruction-assignment/exam/updateInstructionAssignmentExamGivenAnswers';
import QuestionsSidebarMenu from 'Education/components/instruction-assignment/exam/QuestionsSidebarMenu';
import HamburgerButton from 'Common/components/HamburgerButton';
import goToExamQuestionNumber from 'Education/api/instruction-assignment/exam/goToExamQuestionNumber';
import { useOnClickOutside } from 'crooks';
import markInstructionAssignmentExamForApproval from 'Education/api/instruction-assignment/exam/markInstructionAssignmentExamForApproval';
import LinkButton from 'Common/components/LinkButton';
import axios from 'axios';
import notify from 'Common/utils/notify';
import { toast } from 'react-toastify';
import useQueryParameter from 'Common/hooks/useQueryParameter';

const StyledActionBarTitle = styled(ActionBarTitle)`
    display: flex;
    align-items: center;

    button {
        margin-right: 10px;
    }
`;

const QuestionsCounter = styled.div`
    flex: 1 0 auto;
    margin-bottom: 10px;
    color: ${colors.LIGHT_GRAY};
    font-size: 16px;
    line-height: 21px;

    @media screen and (min-width: 420px) {
        margin-right: 20px;
        margin-bottom: 0;
    }
`;

const QuestionContent = styled.div`
    position: relative;
    padding: 20px;
    background: ${colors.DARK_GRAY};
    border-radius: 0 0 4px 4px;
    overflow: hidden;
`;

const ButtonsWrapper = styled.div`
    display: flex;
    margin-top: 40px;

    > button {
        flex: 1;
        margin-right: 10px;

        &:last-child {
            margin-right: 0;
        }
    }

    @media screen and (min-width: 520px) {
        justify-content: space-between;

        > button {
            flex: initial;
            width: auto;
            min-width: 200px;
        }
    }
`;

const AssignmentExamInProgress = ({
    assignment,
    isShowingMistakes,
    isInPreviewMode,
    initialQuestionNumber,
    initialSelectedAnswerIds,
    closeButtonLink,
    onProgress,
    onComplete,
    onNavigateBack,
}) => {
    const [fromTab] = useQueryParameter('fromTab');

    const tabHash = fromTab ? `#${fromTab}` : '';

    const [isLoadingCustomQuestion, setIsLoadingCustomQuestion] = useState(
        false,
    );
    const [isLoadingPreviousQuestion, setIsLoadingPreviousQuestion] = useState(
        false,
    );
    const [isLoadingNextQuestion, setIsLoadingNextQuestion] = useState(false);

    const [questionNumber, setQuestionNumber] = useState(initialQuestionNumber);
    const [selectedAnswerIds, setSelectedAnswerIds] = useState(
        initialSelectedAnswerIds,
    );

    const [isSidebarMenuOpened, setIsSidebarMenuOpened] = useState(false);

    const questions = assignment.examQuestions;
    const questionsCount = questions.length;
    const question = questions.find(
        (question, index) => index + 1 === questionNumber,
    );

    const answeredQuestions = questions.filter(question => {
        const answerIds = question.answers.map(answer => answer.id);

        const intersectingAnswerIds = answerIds.filter(answerId =>
            selectedAnswerIds.includes(answerId),
        );

        return intersectingAnswerIds.length > 0;
    });

    const answeredQuestionNumbers = answeredQuestions.map(
        answeredQuestion =>
            questions.findIndex(
                question => question.id === answeredQuestion.id,
            ) + 1,
    );

    const areAllQuestionsAnswered =
        questions.length === answeredQuestions.length;

    const handleAnswerCheckboxChange = (answerId, isChecked) => {
        setSelectedAnswerIds(prevSelectedAnswerIds => {
            const newSelectedAnswerIds = [...prevSelectedAnswerIds];

            if (isChecked && !newSelectedAnswerIds.includes(answerId)) {
                newSelectedAnswerIds.push(answerId);
            } else if (!isChecked && prevSelectedAnswerIds.includes(answerId)) {
                const index = newSelectedAnswerIds.indexOf(answerId);

                newSelectedAnswerIds.splice(index, 1);
            }

            return newSelectedAnswerIds;
        });
    };

    const source = useMemo(() => axios.CancelToken.source(), []);

    useEffect(
        () => () => {
            source.cancel();
        },
        [source],
    );

    const handlePreviousButtonClick = async () => {
        if (
            !isLoadingPreviousQuestion &&
            !isLoadingNextQuestion &&
            questionNumber > 1
        ) {
            if (!isShowingMistakes) {
                setIsLoadingPreviousQuestion(true);

                try {
                    await updateInstructionAssignmentExamGivenAnswers(
                        assignment.id,
                        selectedAnswerIds,
                        source.token,
                    );
                    await goToPreviousExamQuestion(assignment.id, source.token);
                    await onProgress();

                    setIsLoadingPreviousQuestion(false);
                    setQuestionNumber(
                        prevQuestionNumber => prevQuestionNumber - 1,
                    );
                } catch (error) {
                    if (!axios.isCancel(error)) {
                        setIsLoadingPreviousQuestion(false);

                        notify(
                            'Ein Fehler ist aufgetreten. Bitte versuche es erneut.',
                            {
                                type: toast.TYPE.ERROR,
                            },
                        );
                    }
                }
            } else {
                setQuestionNumber(prevQuestionNumber => prevQuestionNumber - 1);
            }
        }
    };

    const handleNextButtonClick = async () => {
        if (!isLoadingPreviousQuestion && !isLoadingNextQuestion) {
            if (questionNumber < questionsCount) {
                if (!isShowingMistakes) {
                    setIsLoadingNextQuestion(true);

                    try {
                        await updateInstructionAssignmentExamGivenAnswers(
                            assignment.id,
                            selectedAnswerIds,
                            source.token,
                        );
                        await goToNextExamQuestion(assignment.id, source.token);
                        await onProgress();

                        setIsLoadingNextQuestion(false);
                        setQuestionNumber(
                            prevQuestionNumber => prevQuestionNumber + 1,
                        );
                    } catch (error) {
                        if (!axios.isCancel(error)) {
                            setIsLoadingNextQuestion(false);

                            notify(
                                'Ein Fehler ist aufgetreten. Bitte versuche es erneut.',
                                {
                                    type: toast.TYPE.ERROR,
                                },
                            );
                        }
                    }
                } else {
                    setQuestionNumber(
                        prevQuestionNumber => prevQuestionNumber + 1,
                    );
                }
            } else if (
                questionNumber === questionsCount &&
                questionNumber === assignment.currentExamQuestion &&
                (areAllQuestionsAnswered || isShowingMistakes)
            ) {
                // The exam is over
                setIsLoadingNextQuestion(true);

                if (!isShowingMistakes) {
                    try {
                        await updateInstructionAssignmentExamGivenAnswers(
                            assignment.id,
                            selectedAnswerIds,
                            source.token,
                        );

                        if (assignment.isGroupAssignment) {
                            await markInstructionAssignmentExamForApproval(
                                assignment.id,
                                source.token,
                            );
                        } else {
                            await completeSingleInstructionAssignmentExam(
                                assignment.id,
                                source.token,
                            );
                        }

                        await onComplete();
                        setIsLoadingNextQuestion(false);
                    } catch (error) {
                        if (!axios.isCancel(error)) {
                            setIsLoadingNextQuestion(false);

                            notify(
                                'Ein Fehler ist aufgetreten. Bitte versuche es erneut.',
                                {
                                    type: toast.TYPE.ERROR,
                                },
                            );
                        }
                    }
                } else {
                    await onComplete();
                    setIsLoadingNextQuestion(false);
                }
            }
        }
    };

    const handleClickOutsideSidebar = () => {
        setIsSidebarMenuOpened(false);
    };

    const sidebarRef = useOnClickOutside(
        handleClickOutsideSidebar,
        !isSidebarMenuOpened,
    );

    const handleHamburgerButtonClick = () => {
        setIsSidebarMenuOpened(
            prevIsSidebarMenuOpened => !prevIsSidebarMenuOpened,
        );
    };

    const handleQuestionNumberChange = async number => {
        if (!isShowingMistakes) {
            setIsLoadingCustomQuestion(true);

            try {
                await updateInstructionAssignmentExamGivenAnswers(
                    assignment.id,
                    selectedAnswerIds,
                    source.token,
                );
                await goToExamQuestionNumber(
                    assignment.id,
                    number,
                    source.token,
                );
                await onProgress();

                setIsLoadingCustomQuestion(false);
                setQuestionNumber(number);
            } catch (error) {
                if (!axios.isCancel(error)) {
                    setIsLoadingCustomQuestion(false);

                    notify(
                        'Ein Fehler ist aufgetreten. Bitte versuche es erneut.',
                        {
                            type: toast.TYPE.ERROR,
                        },
                    );
                }
            }
        } else {
            setIsLoadingCustomQuestion(false);
            setQuestionNumber(number);
        }
    };

    const isFirstQuestion = questionNumber === 1;
    const isLastQuestion = questionNumber === questionsCount;

    const correctlyAnsweredQuestionNumbers = [];

    questions.forEach((question, index) => {
        const questionAnswerIds = question.answers.map(answer => answer.id);

        const correctAnswerIds = question.answers
            .filter(answer => answer.isCorrect)
            .map(answer => answer.id);

        const givenAnswerIds = selectedAnswerIds.filter(answerId =>
            questionAnswerIds.includes(answerId),
        );

        const intersectingAnswerIds = correctAnswerIds.filter(answerId =>
            givenAnswerIds.includes(answerId),
        );

        if (
            intersectingAnswerIds.length === correctAnswerIds.length &&
            intersectingAnswerIds.length === givenAnswerIds.length
        ) {
            const number = index + 1;

            correctlyAnsweredQuestionNumbers.push(number);
        }
    });

    const renderedCloseButtonLink = (
        <LinkButton to={`${closeButtonLink}${tabHash}`}>Schließen</LinkButton>
    );

    return (
        <>
            <ActionBar>
                <StyledActionBarTitle>
                    <HamburgerButton
                        isOpened={false}
                        onClick={handleHamburgerButtonClick}
                    />
                    {`Test: ${assignment.name}`}
                </StyledActionBarTitle>
                <ActionBarButtonsWrapper>
                    <QuestionsCounter>
                        {`Frage: ${questionNumber} / ${questionsCount}`}
                    </QuestionsCounter>
                    {isShowingMistakes && !isInPreviewMode ? (
                        <Button type="button" onClick={onNavigateBack}>
                            Schließen
                        </Button>
                    ) : (
                        renderedCloseButtonLink
                    )}
                </ActionBarButtonsWrapper>
            </ActionBar>
            <QuestionContent>
                <QuestionTitle question={question} />
                <AnswerChoices
                    answers={question.answers}
                    selectedIds={selectedAnswerIds}
                    onAnswerCheckboxChange={handleAnswerCheckboxChange}
                    isShowingMistakes={isShowingMistakes}
                    isQuestionCorrectlyAnswered={correctlyAnsweredQuestionNumbers.includes(
                        questionNumber,
                    )}
                />
                <ButtonsWrapper>
                    <Button
                        type="button"
                        disabled={
                            isLoadingCustomQuestion ||
                            isLoadingPreviousQuestion ||
                            isLoadingNextQuestion ||
                            isFirstQuestion
                        }
                        onClick={handlePreviousButtonClick}
                    >
                        {isLoadingPreviousQuestion && <Loader size={24} />}
                        Zurück
                    </Button>
                    <Button
                        type="button"
                        disabled={
                            isLoadingCustomQuestion ||
                            isLoadingPreviousQuestion ||
                            isLoadingNextQuestion ||
                            (isLastQuestion && isInPreviewMode) ||
                            (isLastQuestion &&
                                !areAllQuestionsAnswered &&
                                !isShowingMistakes)
                        }
                        title={
                            isLastQuestion &&
                            !areAllQuestionsAnswered &&
                            !isShowingMistakes
                                ? 'Es gibt unbeantwortete Fragen'
                                : undefined
                        }
                        onClick={handleNextButtonClick}
                    >
                        {isLoadingNextQuestion && <Loader size={24} />}
                        Weiter
                    </Button>
                </ButtonsWrapper>
                {isSidebarMenuOpened && (
                    <QuestionsSidebarMenu
                        elementRef={sidebarRef}
                        questions={questions}
                        questionNumber={questionNumber}
                        isShowingMistakes={isShowingMistakes}
                        correctlyAnsweredQuestionNumbers={
                            correctlyAnsweredQuestionNumbers
                        }
                        isLoading={
                            isLoadingCustomQuestion ||
                            isLoadingPreviousQuestion ||
                            isLoadingNextQuestion
                        }
                        answeredQuestionNumbers={answeredQuestionNumbers}
                        onQuestionNumberChange={handleQuestionNumberChange}
                    />
                )}
            </QuestionContent>
        </>
    );
};

AssignmentExamInProgress.defaultProps = {
    isInPreviewMode: false,
    closeButtonLink: '/dashboard',
    onNavigateBack: () => {},
};

AssignmentExamInProgress.propTypes = {
    assignment: PropTypes.object.isRequired,
    isShowingMistakes: PropTypes.bool.isRequired,
    isInPreviewMode: PropTypes.bool,
    initialQuestionNumber: PropTypes.number.isRequired,
    initialSelectedAnswerIds: PropTypes.array.isRequired,
    closeButtonLink: PropTypes.string,
    onProgress: PropTypes.func.isRequired,
    onComplete: PropTypes.func.isRequired,
    onNavigateBack: PropTypes.func,
};

export default AssignmentExamInProgress;
